import { SalesforceProduct } from "@shared/services";
import * as T from "@shared/types";
import { AxiosRequestConfig } from "axios";
import { axios } from ".";
import i18n from '@client-shared/lib/i18n';
import { handleDisplayErrorData } from './utilities/handleDisplayErrorData';
import { throwError } from "./utilities/throwing";

const BC_PRODUCTS_BASE_URL = "/api/v1/products/beanz-connect-products";
const BEANZ_PRODUCTS_BASE_URL = "/api/v1/products/beanz-products";
const VENDOR_PRODUCTS_BASE_URL = "/api/v1/products/vendor-products";

export const postProductConnections = async (paramsArray: T.CreateConnectionParams[]) => {
  const response = await axios.post<{ data: T.Connection[] }>(BC_PRODUCTS_BASE_URL, paramsArray);
  return response.data.data;
};

export const putProductConnections = async (connections: T.Connection[]) => {
  const response = await axios.put<{ data: T.ProductConnection[] }>(BC_PRODUCTS_BASE_URL, connections);
  return response.data.data;
};

export const deleteProductConnection = async (connection: T.Connection) => {
  const url = `${BC_PRODUCTS_BASE_URL}/${connection.id}`;
  const response = await axios.delete<{ data: T.Connection }>(url, { data: connection });
  return response.data.data;
};

export const getInstallationVariant = async (installationId: string, variantId: string, productId?: string) => {
  const url = `${VENDOR_PRODUCTS_BASE_URL}/installations/${installationId}/variants/${variantId}`;
  const config: AxiosRequestConfig = { params: { productId: productId } };
  try {
    const response = await axios.get<{ data: T.Variant }>(url, config);
    return response.data.data;
  } catch (e) {
    throwError("Error in fetching variant", e);
  }
};

export const getInstallationProducts = async (
  vendor: T.Vendor,
  installationId: string,
  optionalParams?: { title?: string; limit?: number; publishedStatus?: string }
) => {
  const url = `${BEANZ_PRODUCTS_BASE_URL}/installations/${installationId}`;
  const params = {
    ...optionalParams,
    vendorRegion: vendor.region,
  };
  const response = await axios.get<{ data: T.Product[] }>(url, { params });
  return response.data.data;
};

export const getServiceProducts = async (vendor: T.Vendor, serviceId: string) => {
  const url = `${BEANZ_PRODUCTS_BASE_URL}/${serviceId}`;
  const config: AxiosRequestConfig = { params: { vendorRegion: vendor.region } };
  const response = await axios.get<{ data: T.Product[] }>(url, config);
  return response.data.data;
};

export const postProducts = async (serviceId: string, accessToken: string, products: Partial<SalesforceProduct>[]) => {
  const config = { headers: { "x-access-token": accessToken } };
  const url = `${BEANZ_PRODUCTS_BASE_URL}/${serviceId}`;
  return await axios.post<SalesforceProduct[]>(url, products, config);
};

export const getGlobalProductConfig = async (vendorRegion: string) => {
  const url = `${BEANZ_PRODUCTS_BASE_URL}/salesforce/product-config`;
  const config: AxiosRequestConfig = { params: { vendorRegion: vendorRegion } };
  try {
    const response = await axios.get<{ data: T.ProductConfig }>(url, config);
    return response.data.data;
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.getProductInventoryConfig.message"),
      i18n.t("apiErrorMessages.getProductInventoryConfig.title"),
      e as Error
    );
    throw e;
  }
};

export const getLocalisedProductConfig = async (locale: T.Locale) => {
  const url = `${BEANZ_PRODUCTS_BASE_URL}/product-attributes/translations-group-by-category/${locale}`;
  try {
    const response = await axios.get<{ data: T.PimProductsCategories }>(url);
    return response.data.data;
  } catch (e) {
    console.log("Error in fetching localised vendor config", e);
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.getProductMultipleBagConfig.message"),
      i18n.t("apiErrorMessages.getProductMultipleBagConfig.title"),
      e as Error
    );
    throw e;
  }
};
